import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingIndicator } from '../../components/Spinner';
import { type TicketAccess, openTicketAccess } from './accessApi';

/**
 * Der Weg in einen Vorgang ohne Konto -- F4-35.
 *
 * **Zwei Felder statt einer Anmeldung.** Die Kunden dieses Systems haben in
 * aller Regel keinen Portalzugang. Sie haben eine Mail mit einer
 * Vorgangsnummer -- und die genuegt zusammen mit ihrer Adresse.
 *
 * **Aus dem Link wird ein Klick.** Steht beides in der Adresszeile
 * (`/portal?vorgang=T-2026-000123&adresse=anna@kunde.de`), fuellt sich die
 * Maske und schickt sich einmal selbst ab. Der Link in jeder ausgehenden
 * Ticketmail sieht genau so aus.
 *
 * **Die Maske bleibt trotzdem stehen.** Wer den Link von Hand kuerzt, sich
 * vertippt oder die Mail nicht mehr hat, soll nicht vor einer leeren Seite
 * stehen -- und wer abgewiesen wurde, soll sehen, was er eingegeben hat.
 *
 * Was bei falschen Angaben passiert, entscheidet der Server: Er antwortet
 * auf eine unbekannte Nummer und auf eine falsche Adresse wortgleich und
 * sperrt nach wenigen Fehlversuchen. Diese Datei prueft deshalb **nichts**
 * ausser "beide Felder ausgefuellt" -- jede Pruefung hier waere eine zweite
 * Wahrheit ueber denselben Zugang.
 */
export function TicketAccessForm({
  initialEmail,
  initialTicketNumber,
  onOpened,
}: {
  initialEmail: string;
  initialTicketNumber: string;
  onOpened: (access: TicketAccess) => void;
}) {
  const { t } = useTranslation();

  const [email, setEmail] = useState(initialEmail);
  const [ticketNumber, setTicketNumber] = useState(initialTicketNumber);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Einmal, nicht bei jeder Aenderung: Ohne diesen Merker schickte sich die
  // Maske nach einer Abweisung sofort wieder ab -- und zaehlte den
  // Fehlversuch in einer Schleife hoch, bis die Sperre greift.
  const submitted = useRef(false);

  const send = async (withEmail: string, withNumber: string) => {
    setBusy(true);
    setError(null);
    try {
      onOpened(await openTicketAccess(withEmail.trim(), withNumber.trim()));
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (submitted.current) return;
    if (!initialEmail.trim() || !initialTicketNumber.trim()) return;
    submitted.current = true;
    void send(initialEmail, initialTicketNumber);
    // Der Lauf haengt an den Angaben aus dem Link, und die aendern sich
    // waehrend der Sitzung nicht.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEmail, initialTicketNumber]);

  const ready = email.trim().length > 0 && ticketNumber.trim().length > 0;

  return (
    <section className="card">
      <h2>{t('portal.access.title')}</h2>
      <p className="table__muted">{t('portal.access.hint')}</p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) void send(email, ticketNumber);
        }}
      >
        <div className="field">
          <label className="field__label" htmlFor="access-email">
            {t('portal.access.email')}
          </label>
          <input
            id="access-email"
            type="email"
            autoComplete="email"
            className="field__control"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="access-number">
            {t('portal.access.ticketNumber')}
          </label>
          <input
            id="access-number"
            className="field__control"
            placeholder={t('portal.access.ticketNumberPlaceholder')}
            value={ticketNumber}
            onChange={(event) => {
              setTicketNumber(event.target.value);
            }}
          />
          <p className="field__hint">{t('portal.access.ticketNumberHint')}</p>
        </div>

        {error ? <ErrorNotice error={error} /> : null}
        {busy ? <LoadingIndicator /> : null}

        <Button type="submit" variant="primary" disabled={!ready} busy={busy}>
          {t('portal.access.submit')}
        </Button>
      </form>
    </section>
  );
}
